// Guided, builder-backed Report Templates manager.
// Admins build a template by picking a data source and toggling VALID
// dimensions/measures/filters (no raw typing), preview it live, and save it.
import { useEffect, useMemo, useState } from "react";
import {
  FileText, Loader2, Play, Save, Trash2, Plus, Pencil, Download, X,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ReportChart } from "./_charts";
import { RangeControl, rangeParams, isoToday, isoDaysAgo } from "./_daterange";

const CHARTS = [
  { id: "table", label: "Table only" },
  { id: "bar", label: "Bar" },
  { id: "line", label: "Line" },
  { id: "pie", label: "Pie" },
];

const EMPTY = {
  name: "", description: "", source: "cdrs",
  dimensions: ["date"], measures: ["count"], filters: {},
  chart: "bar", range_str: "30d",
};

export default function ReportTemplatesPage() {
  const [sources, setSources] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);   // null | "new" | template id
  const [form, setForm] = useState(EMPTY);
  const [rng, setRng] = useState("30d");
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(isoToday());
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/reports/sources").then((r) => setSources(r.data.sources || []))
      .catch((e) => toast.error(formatErr(e)));
    loadTemplates();
  }, []); // eslint-disable-line

  async function loadTemplates() {
    setLoading(true);
    try {
      const r = await api.get("/reports/templates");
      setTemplates(r.data.templates || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }

  const src = useMemo(() => sources.find((s) => s.id === form.source), [sources, form.source]);

  function openNew() {
    setForm(EMPTY); setRng("30d"); setPreview(null); setEditing("new");
  }
  function openEdit(t) {
    setForm({
      name: t.name, description: t.description || "", source: t.source,
      dimensions: t.dimensions || [], measures: t.measures || ["count"],
      filters: t.filters || {}, chart: t.chart || "bar", range_str: t.range_str || "30d",
    });
    setRng(t.range_str || "30d"); setPreview(null); setEditing(t.id);
  }
  function cancel() { setEditing(null); setPreview(null); }

  function switchSource(id) {
    const s = sources.find((x) => x.id === id);
    setForm((f) => ({
      ...f, source: id,
      dimensions: s?.dimensions?.[0] ? [s.dimensions[0].id] : [],
      measures: s?.measures?.[0] ? [s.measures[0].id] : ["count"],
      filters: {},
    }));
    setPreview(null);
  }
  function toggle(field, v) {
    setForm((f) => ({
      ...f,
      [field]: f[field].includes(v) ? f[field].filter((x) => x !== v) : [...f[field], v],
    }));
  }

  async function runPreview() {
    if (!src) return;
    setBusy(true); setPreview(null);
    try {
      const r = await api.post("/reports/custom/run", {
        source: form.source, dimensions: form.dimensions, measures: form.measures,
        filters: form.filters, ...rangeParams(rng, from, to),
      });
      setPreview({ ...r.data, chart: form.chart });
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }

  async function save() {
    if (!form.name.trim()) { toast.error("Give the template a name"); return; }
    if (!form.measures.length) { toast.error("Pick at least one measure"); return; }
    const body = { ...form, name: form.name.trim(), range_str: rng };
    try {
      if (editing === "new") await api.post("/reports/templates", body);
      else await api.put(`/reports/templates/${editing}`, body);
      toast.success("Template saved");
      setEditing(null); setPreview(null);
      loadTemplates();
    } catch (e) { toast.error(formatErr(e)); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this template? Any schedules using it will be paused.")) return;
    try { await api.delete(`/reports/templates/${id}`); toast.success("Deleted"); loadTemplates(); }
    catch (e) { toast.error(formatErr(e)); }
  }

  async function download(t, fmt) {
    try {
      const r = await api.get(`/reports/templates/${t.id}/export?fmt=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${t.name}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) { toast.error(formatErr(e)); }
  }

  return (
    <div className="px-4 sm:px-6 py-6 max-w-7xl" data-testid="report-templates-page">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" /> Report Templates
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Build reusable reports from valid fields — no guessing. Preview, save, download, or attach to a schedule.
          </p>
        </div>
        {!editing && (
          <Button onClick={openNew} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5" data-testid="rt-new">
            <Plus className="w-4 h-4" /> New template
          </Button>
        )}
      </div>

      {/* Editor */}
      {editing && (
        <div className="grid lg:grid-cols-[360px_1fr] gap-5 mb-6">
          <Card className="p-5 space-y-4 self-start" data-testid="rt-editor">
            <div>
              <Label className="text-xs uppercase font-bold tracking-wider mb-1 block">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Daily Agent Productivity" data-testid="rt-name" />
            </div>
            <div>
              <Label className="text-xs uppercase font-bold tracking-wider mb-1 block">Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Optional" data-testid="rt-desc" />
            </div>
            <div>
              <Label className="text-xs uppercase font-bold tracking-wider mb-2 block">Data source</Label>
              <Select value={form.source} onValueChange={switchSource}>
                <SelectTrigger data-testid="rt-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {sources.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase font-bold tracking-wider mb-2 block">Group by (dimensions)</Label>
              <div className="flex flex-wrap gap-1.5">
                {src?.dimensions.map((d) => (
                  <button key={d.id} data-testid={`rt-dim-${d.id}`} onClick={() => toggle("dimensions", d.id)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors
                      ${form.dimensions.includes(d.id) ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/40"}`}>
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase font-bold tracking-wider mb-2 block">Metrics (measures)</Label>
              <div className="flex flex-wrap gap-1.5">
                {src?.measures.map((m) => (
                  <button key={m.id} data-testid={`rt-meas-${m.id}`} onClick={() => toggle("measures", m.id)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors
                      ${form.measures.includes(m.id) ? "bg-secondary text-secondary-foreground border-secondary" : "bg-card border-border hover:border-secondary/40"}`}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {src?.filters?.length > 0 && (
              <div>
                <Label className="text-xs uppercase font-bold tracking-wider mb-2 block">Filters (optional)</Label>
                <div className="space-y-2">
                  {src.filters.map((f) => (
                    <div key={f} className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-28 shrink-0">{f}</span>
                      <Input data-testid={`rt-filter-${f}`} value={form.filters[f] || ""}
                        onChange={(e) => setForm({ ...form, filters: { ...form.filters, [f]: e.target.value } })}
                        placeholder="any" className="h-8 text-xs flex-1" />
                      {form.filters[f] && (
                        <button onClick={() => { const { [f]: _, ...rest } = form.filters; setForm({ ...form, filters: rest }); }}
                          className="text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs uppercase font-bold tracking-wider mb-1 block">Default range</Label>
                <RangeControl rng={rng} setRng={setRng} from={from} to={to} setFrom={setFrom} setTo={setTo} testid="rt-range" />
              </div>
              <div>
                <Label className="text-xs uppercase font-bold tracking-wider mb-1 block">Chart</Label>
                <Select value={form.chart} onValueChange={(v) => setForm({ ...form, chart: v })}>
                  <SelectTrigger className="h-8 text-xs" data-testid="rt-chart"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CHARTS.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-2 pt-2 border-t border-border">
              <Button onClick={runPreview} disabled={busy || !form.measures.length} variant="outline" className="flex-1 gap-1.5 h-9" data-testid="rt-preview">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Preview
              </Button>
              <Button onClick={save} className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5 h-9" data-testid="rt-save">
                <Save className="w-4 h-4" /> Save
              </Button>
              <Button onClick={cancel} variant="ghost" className="h-9" data-testid="rt-cancel">Cancel</Button>
            </div>
          </Card>

          <Card className="p-5 min-w-0" data-testid="rt-preview-card">
            <h2 className="font-display text-lg font-bold mb-3">Live preview</h2>
            {busy && <div className="h-[260px] flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>}
            {!busy && !preview && <div className="h-[260px] flex items-center justify-center text-sm text-muted-foreground italic">Hit <span className="font-mono mx-1">Preview</span> to see live data.</div>}
            {!busy && preview && (
              <>
                {form.chart !== "table" && <ReportChart kind={form.chart} columns={preview.columns} rows={preview.rows} height={280} />}
                <div className="mt-4 overflow-x-auto rounded-md border border-border max-h-[360px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 sticky top-0">
                      <tr>{preview.columns.map((c) => <th key={c.id} className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider">{c.label}</th>)}</tr>
                    </thead>
                    <tbody>
                      {preview.rows.length === 0 && <tr><td colSpan={preview.columns.length} className="px-3 py-8 text-center text-sm text-muted-foreground italic">No rows in range.</td></tr>}
                      {preview.rows.map((r, i) => (
                        <tr key={i} className="odd:bg-card even:bg-muted/20 border-t border-border/60">
                          {preview.columns.map((c) => <td key={c.id} className="px-3 py-2 font-mono text-xs">{r[c.id] ?? ""}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Card>
        </div>
      )}

      {/* Templates list */}
      <Card className="p-5" data-testid="rt-list">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold">Saved templates</h3>
          <Badge variant="outline" className="text-[10px]">{templates.length}</Badge>
        </div>
        {loading ? (
          <div className="h-24 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : templates.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No templates yet — click <span className="font-medium">New template</span> to build one.</p>
        ) : (
          <ul className="divide-y divide-border/60">
            {templates.map((t) => (
              <li key={t.id} className="flex items-center gap-3 py-3" data-testid={`rt-row-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate flex items-center gap-2">
                    {t.name}
                    {t.builtin && <Badge variant="secondary" className="text-[9px] uppercase tracking-wide">Built-in</Badge>}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {t.builtin
                      ? `${t.description || "Default report"} · ${t.range_str}`
                      : `${t.source} · ${(t.dimensions || []).join(", ") || "no groups"} · ${(t.measures || []).join(", ")} · ${t.range_str} · ${t.chart}`}
                  </div>
                </div>
                <Select onValueChange={(fmt) => download(t, fmt)}>
                  <SelectTrigger className="h-8 w-[120px] text-xs" data-testid={`rt-dl-${t.id}`}>
                    <Download className="w-3.5 h-3.5 mr-1" /><SelectValue placeholder="Download" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="csv">CSV</SelectItem>
                    <SelectItem value="xlsx">Excel</SelectItem>
                    <SelectItem value="json">JSON</SelectItem>
                  </SelectContent>
                </Select>
                {!t.builtin && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => openEdit(t)} className="h-8 gap-1 text-xs" data-testid={`rt-edit-${t.id}`}>
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </Button>
                    <button onClick={() => remove(t.id)} className="text-muted-foreground hover:text-red-600 p-1" data-testid={`rt-del-${t.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
